import { CompareFn, sort } from './sort'
export { asc } from './sort'

/**
 * Get an element at a given {@link index} for an {@link array}
 * If the index is out of range, return undefined
 *
 * @param array
 * @param index
 * @returns
 */
export function at<T>(array: T[], index: number): T | undefined {
  return index < 0 || index >= array.length ? undefined : array[index]
}

export type Predicate<T> = (value: T) => boolean

/**
 * Generates an array of numbers within a specified range.
 * Includes the start ane end numbers.
 *
 * Keep in mind: the length of the array will be (end - start + 1)
 *
 * @param start The starting value of the range. This number is included in the returned array.
 * @param end The ending value of the range. This number is included in the returned array.
 * @returns An array of numbers starting from `start` and ending at `end`. Each element in the array is a consecutive number from the start value, incrementing by 1 until the end value is reached.
 *
 * @example
 * // For generating a range from 1 to 5
 * const numbers = range(1, 5);
 * console.log(numbers); // Output: [1, 2, 3, 4, 5]
 *
 * @example
 * // For generating a range from -2 to 2
 * const numbers = range(-2, 2);
 * console.log(numbers); // Output: [-2, -1, 0, 1, 2]
 */
export function range(start: number, end: number): number[] {
  if (start > end) {
    return []
  }
  return Array(end - start + 1)
    .fill(0)
    .map((_, index) => start + index)
}

/**
 * Given an {@link array}, return a map of arrays where the elements are
 * grouped by {@link keyOf}
 */
export function groupBy<T>(array: T[], keyOf: (el: T) => string): Record<string, T[]> {
  const map: Record<string, T[]> = {}
  for (var el of array) {
    let key = keyOf(el)
    if (map[key]) {
      map[key].push(el)
    } else {
      map[key] = [el]
    }
  }
  return map
}

export function findLast<T>(array: T[], predicate: (el: T) => boolean): T | undefined {
  for (let i = array.length - 1; i >= 0; i--) {
    if (predicate(array[i])) {
      return array[i]
    }
  }
  return undefined
}

export type PaginatedPage<T> = {
  number: number
  items: T[]
  isFirst: boolean
  isLast: boolean
}

export function paginate<T>(array: T[], size: number): Array<PaginatedPage<T>> {
  if (size <= 0) {
    throw new Error('Chunk size must be positive')
  }

  const pages: Array<PaginatedPage<T>> = []

  if (array.length == 0) {
    return [{ number: 1, items: [], isFirst: true, isLast: true }]
  }

  for (let i = 0; i < array.length; i += size) {
    pages.push({
      number: i + 1,
      isFirst: false,
      isLast: false,
      items: array.slice(i, i + size)
    })
  }

  pages[0].isFirst = true
  pages[pages.length - 1].isLast = true

  return pages
}

/**
 * Given an {@link array}, return a map where the the key
 * is the {@link keyOf} property of the value
 *
 * If there are duplicate elements with the same key, the last element be used
 */
export function keyBy<T>(array: T[], keyOf: (el: T) => string): Record<string, T> {
  const map: Record<string, T> = {}
  for (var el of array) map[keyOf(el)] = el
  return map
}

/**
 * Return a new array with the duplicates from {@link array} removed
 */
export function unique<T>(array: T[]): T[] {
  return Array.from(new Set(array))
}

export function uniqueBy<T>(array: T[], keyOf: (el: T) => string): T[] {
  return Array.from(new Map(array.map((el) => [keyOf(el), el] as const)).values())
}
